import * as React from "react";
import { useState, useCallback } from "react";
import { makeStyles, createStyles, Theme, Typography, IconButton } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import ReactMarkdown from "react-markdown";
import Section from "../Section";
import {
  SECTION_WHITE,
  TEXT_BODY,
  TEXT_HEADING,
  SECTION_PADDING_Y,
} from "../../styles/homepageSections";

type StrapiImage = {
  localFile?: {
    childImageSharp?: { gatsbyImageData: IGatsbyImageData };
    publicURL?: string;
  };
};

export type ReferenceItemData = {
  logo?: StrapiImage | null;
  description?: string | null;
  avatar?: StrapiImage | null;
  name?: string | null;
  role?: string | null;
};

export type CustomerReferencesData = {
  subheadline?: string | null;
  items?: ReferenceItemData[] | null;
};

const hasText = (value?: string | null) =>
  Boolean(value && value.replace(/<[^>]*>/g, "").trim());

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const StrapiMedia: React.FC<{
  image?: StrapiImage | null;
  alt: string;
  className?: string;
  imgClassName?: string;
}> = ({ image, alt, className, imgClassName }) => {
  const gatsbyImage = image?.localFile?.childImageSharp?.gatsbyImageData;
  const url = image?.localFile?.publicURL;

  if (gatsbyImage) {
    return <GatsbyImage image={gatsbyImage} alt={alt} className={className} imgClassName={imgClassName} />;
  }
  if (url) {
    return <img src={url} alt={alt} className={imgClassName || className} />;
  }
  return null;
};

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    section: {
      padding: SECTION_PADDING_Y.mobile,
      [t.breakpoints.up("md")]: {
        padding: SECTION_PADDING_Y.desktop,
      },
    },
    subheadline: {
      textAlign: "center",
      color: t.palette.primary.main,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      marginBottom: 40,
      [t.breakpoints.up("md")]: {
        fontSize: 13,
        marginBottom: 48,
      },
    },
    carousel: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      [t.breakpoints.up("md")]: {
        gap: 24,
      },
    },
    navButton: {
      flexShrink: 0,
      width: 48,
      height: 48,
      border: `1px solid ${t.palette.primary.main}`,
      color: t.palette.primary.main,
      backgroundColor: "#fff",
      transition: "background-color 0.2s, color 0.2s",
      "&:hover": {
        backgroundColor: t.palette.primary.main,
        color: "#fff",
      },
      [t.breakpoints.down("xs")]: {
        width: 40,
        height: 40,
      },
    },
    navHidden: {
      visibility: "hidden",
      pointerEvents: "none",
    },
    slide: {
      flex: 1,
      maxWidth: 720,
      minWidth: 0,
      textAlign: "left",
      padding: "0 8px",
    },
    logoWrap: {
      display: "flex",
      justifyContent: "center",
      marginBottom: 32,
    },
    logoImage: {
      width: 150,
      maxWidth: "100%",
      height: "auto",
      display: "block",
      objectFit: "contain",
    },
    quote: {
      color: TEXT_BODY,
      fontSize: 18,
      lineHeight: 1.55,
      marginBottom: 40,
      "& p": {
        margin: 0,
      },
      "& strong": {
        color: t.palette.primary.main,
        fontWeight: 600,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 20,
        lineHeight: 1.6,
      },
    },
    attribution: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      textAlign: "left",
      gap: 16,
    },
    avatar: {
      width: 56,
      height: 56,
      flexShrink: 0,
      backgroundColor: t.palette.common.black,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
      fontWeight: 600,
      overflow: "hidden",
      "& img": {
        width: "100%",
        height: "100%",
        objectFit: "cover",
      },
    },
    personName: {
      color: TEXT_HEADING,
      fontWeight: 600,
      fontSize: 16,
      lineHeight: 1.3,
    },
    personRole: {
      color: t.palette.primary.main,
      fontSize: 14,
      lineHeight: 1.4,
      marginTop: 4,
    },
    dots: {
      display: "flex",
      justifyContent: "center",
      gap: 8,
      marginTop: 32,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      padding: 0,
      border: "none",
      backgroundColor: "rgba(234, 22, 116, 0.25)",
      cursor: "pointer",
      transition: "background-color 0.2s, transform 0.2s",
    },
    dotActive: {
      backgroundColor: t.palette.primary.main,
      transform: "scale(1.15)",
    },
  })
);

const ReferenceSlide: React.FC<{ item: ReferenceItemData; classes: ReturnType<typeof useStyles> }> = ({
  item,
  classes,
}) => (
  <div className={classes.slide}>
    {(item.logo?.localFile?.publicURL || item.logo?.localFile?.childImageSharp) && (
      <div className={classes.logoWrap}>
        <StrapiMedia image={item.logo} alt="" className={classes.logoImage} imgClassName={classes.logoImage} />
      </div>
    )}

    {hasText(item.description) && (
      <div className={classes.quote}>
        <ReactMarkdown
          linkTarget="_blank"
          components={{
            p: ({ children }) => <p>{children}</p>,
            strong: ({ children }) => <strong>{children}</strong>,
          }}
        >
          {item.description!}
        </ReactMarkdown>
      </div>
    )}

    {(hasText(item.name) || hasText(item.role)) && (
      <div className={classes.attribution}>
        <div className={classes.avatar}>
          {item.avatar?.localFile ? (
            <StrapiMedia image={item.avatar} alt={item.name || ""} />
          ) : (
            hasText(item.name) && getInitials(item.name!)
          )}
        </div>
        <div>
          {hasText(item.name) && (
            <Typography component="p" className={classes.personName}>
              {item.name}
            </Typography>
          )}
          {hasText(item.role) && (
            <Typography component="p" className={classes.personRole}>
              {item.role}
            </Typography>
          )}
        </div>
      </div>
    )}
  </div>
);

const CustomerReferencesSection: React.FC<{ data?: CustomerReferencesData | null }> = ({
  data,
}) => {
  const classes = useStyles();
  const items = data?.items?.filter((item) => item && (hasText(item.description) || hasText(item.name))) ?? [];

  const [index, setIndex] = useState(0);
  const count = items.length;

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  if (!data || (items.length === 0 && !hasText(data.subheadline))) {
    return null;
  }

  const showNav = count > 1;
  const current = items[index];

  return (
    <Section color={SECTION_WHITE} className={classes.section}>
      {hasText(data.subheadline) && (
        <Typography component="p" className={classes.subheadline}>
          {data.subheadline}
        </Typography>
      )}

      {count > 0 && current && (
        <>
          <div className={classes.carousel}>
            <IconButton
              className={[classes.navButton, !showNav && classes.navHidden].filter(Boolean).join(" ")}
              onClick={() => goTo(index - 1)}
              aria-label="Previous reference"
              disableRipple
            >
              <ChevronLeftIcon />
            </IconButton>

            <ReferenceSlide item={current} classes={classes} />

            <IconButton
              className={[classes.navButton, !showNav && classes.navHidden].filter(Boolean).join(" ")}
              onClick={() => goTo(index + 1)}
              aria-label="Next reference"
              disableRipple
            >
              <ChevronRightIcon />
            </IconButton>
          </div>

          {showNav && (
            <div className={classes.dots} role="tablist" aria-label="References">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Reference ${i + 1}`}
                  className={[classes.dot, i === index && classes.dotActive].filter(Boolean).join(" ")}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
};

export default CustomerReferencesSection;
